import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TrackingFacadeService } from './tracking/tracking-facade.service';

@Injectable({ providedIn: 'root' })
export class ModalService
{
    private modalService: NgbModal;
    private modal: NgbModalRef;
    private router: Router;
    private navigationEndSubscription: Subscription;
    private readonly trackingFacadeService: TrackingFacadeService;

    public constructor(modalService: NgbModal, router: Router, trackingFacadeService: TrackingFacadeService)
    {
        this.modalService = modalService;
        this.router = router;
        this.trackingFacadeService = trackingFacadeService;

        this.setSubscriptions();
    }

    public open(content: any, options: NgbModalOptions): NgbModalRef
    {
        if (this.modal != null)
        {
            this.dismissModal('closing existing');
        }
        
        this.modal = this.modalService.open(content, options);
        this.trackModal(this.modal);
        this.modal.result.then(
            (result) => { },
            (reason) => { }
        );

        return this.modal;
    }

    /**
     * Centrally tracks open/close for every ng-bootstrap modal (`ngb-modal-window`) opened through
     * this service, as spec modal events (`modal_shown` / `modal_closed`). Bound to `.shown` / `.hidden`
     * so backdrop clicks and ESC dismissals are captured too. The modal name is a best-effort read of
     * the modal's heading text.
     */
    private trackModal(modalRef: NgbModalRef): void
    {
        // Captured on open so it is still available when the modal DOM is gone on close.
        let modalName = 'modal';

        modalRef.shown.subscribe(() =>
        {
            modalName = this.resolveModalName();
            this.trackingFacadeService.modalOpen(modalName);
        });

        modalRef.hidden.subscribe(() =>
        {
            this.trackingFacadeService.modalClose(modalName);
        });
    }

    /** Best-effort human-readable name for the currently shown modal, taken from its heading text. */
    private resolveModalName(): string
    {
        const win = document.querySelector('ngb-modal-window');
        const heading = win?.querySelector('h1, h2, h3, h4, h5, h6');
        return heading?.textContent?.trim() || 'modal';
    }

    public dismissModal(reason?: any): void
    {
        if (this.modal == null)
        {
            return;
        }

        this.modal.close(reason);
        this.modal = null;
    }

    private setSubscriptions(): void
    {
        this.navigationEndSubscription = this.router.events.pipe(
            filter((event: any) => event instanceof NavigationEnd))
            .subscribe((event => {
                this.dismissModal('router navigation');
            }));
    }
}
